/**
 * CreateRevisionAreaDialog — the "Create revision session" button's dialog on
 * the CoachCard.
 *
 * Two tabs, each a full configurator:
 *   - By content — `SessionCreator` (course → module → topic → lesson
 *     picker + QuestionTypeFilter + max-questions slider). Familiar flow.
 *   - By topic   — `TopicSessionCreator` (curriculum-tree picker + the SAME
 *     filter + slider). Differs only in how steps are sourced; visual
 *     language matches.
 *
 * Lesson-picker is the default tab because it's the familiar flow; swap
 * once usage data says the topic picker is the common path.
 */
import { useState } from 'react';
import type { RevisableCourse, RevisablePersonalLesson } from '@formency/shared';
import { Dialog, Tabs } from '@/interfaces/learner/ui';
import SessionCreator from './SessionCreator';
import TopicSessionCreator from './TopicSessionCreator';

export interface CreateRevisionAreaDialogProps {
  isOpen: boolean;
  onClose: () => void;
  /** Revisable content for the "By content" tab (as returned by /me/content/revisable). */
  courses: RevisableCourse[];
  personalLessons: RevisablePersonalLesson[];
  /** Called when the learner finishes the "By content" configurator. */
  onStartByLessons: (config: {
    lessonIds: string[];
    questionTypes: string[];
    maxQuestions: number;
  }) => void;
  /** Called when the learner finishes the "By topic" configurator. */
  onStartByTopic: (config: {
    nodeIds: string[];
    questionTypes: string[];
    maxQuestions: number;
  }) => void;
  isCreating: boolean;
  error?: string | null;
}

type TabId = 'content' | 'topic';

export default function CreateRevisionAreaDialog({
  isOpen,
  onClose,
  courses,
  personalLessons,
  onStartByLessons,
  onStartByTopic,
  isCreating,
  error,
}: CreateRevisionAreaDialogProps) {
  const [activeTab, setActiveTab] = useState<TabId>('content');

  return (
    <Dialog isOpen={isOpen} onClose={onClose} title="Create revision session">
      <Tabs
        selectedKey={activeTab}
        onSelectionChange={(key) => setActiveTab(key as TabId)}
        tabs={[
          {
            id: 'content',
            label: 'By content',
            content: () => (
              <SessionCreator
                courses={courses}
                personalLessons={personalLessons}
                onStart={onStartByLessons}
                onCancel={onClose}
                isCreating={isCreating}
                error={error}
              />
            ),
          },
          {
            id: 'topic',
            label: 'By subject',
            content: () => (
              <TopicSessionCreator
                onStart={onStartByTopic}
                onCancel={onClose}
                isCreating={isCreating}
                error={error}
              />
            ),
          },
        ]}
      />
    </Dialog>
  );
}
